/**
 * Ошибки API как `application/problem+json` (RFC 9457).
 *
 * Правило одно и оно из docs/API.md §4: **ветвиться в коде по `code`**, не по
 * статусу и не по тексту. Тот же `code` — единственный ключ, по которому
 * ошибка переводится: строка лежит в каталоге локали под `error.code.<код>`, и
 * своей таблицы соответствий у кабинета нет. Таблица разъехалась бы с
 * контрактом молча, а увидел бы это клиент.
 *
 * Показывать «Error 500» нельзя нигде. Три предложения максимум: что
 * случилось, что это значит для числа на экране, что делать.
 *
 * ─── Чей текст показывается ─────────────────────────────────────────────────
 *
 * `detail` сервера точнее любой нашей строки: он знает числа и сроки —
 * сколько вопросов осталось в квоте, до какого числа идут замеры. Но приходит
 * он на языке сервиса, а не клиента: согласования языка в контракте пока нет,
 * оно приезжает PR-ом контракта вместе с market-конфигом.
 *
 * Поэтому правило такое: `detail` показывается там, где язык клиента совпадает
 * с языком сервиса, то есть на языке-источнике рынка (в РФ — русский, и там всё
 * ровно как было). На остальных языках берётся строка каталога по машинному
 * коду; `detail` остаётся последним рубежом — только если строки нет вовсе.
 * Русская фраза посреди арабского экрана хуже нашей же формулировки без чисел.
 */

import { locale, market, t, hasMessage } from '../i18n';

import type { components } from './schema';
import { LEGAL_ENTITY } from '@shared/src/product';

export type Problem = components['schemas']['Problem'];

/** Машинные коды, на которые клиент реагирует поведением, а не текстом. */
export const CODES = {
  /** Access истёк — сходить в POST /auth/refresh и повторить запрос. */
  sessionExpired: 'session_expired',
  /** Куки нет или подпись не сходится — на экран входа. */
  unauthenticated: 'unauthenticated',
  /** Нужен второй фактор — продолжение на POST /auth/login/mfa. */
  mfaRequired: 'mfa_required',
  /** Нужно подтверждение на действии — заголовок X-Seeno-Step-Up. */
  stepUpRequired: 'step_up_required',
  /** Превышена частота — ждать Retry-After. */
  rateLimited: 'rate_limited',
} as const;

/**
 * Запасной текст по машинному коду.
 *
 * Запасной — не значит служебный. Часть этих строк была написана словами
 * протокола: «Тот же ключ пришёл с другим телом запроса», «Слишком большой
 * запрос», «Запрос пришёл с неизвестного адреса». Человек не думает телами и
 * запросами, а видит он эти строки ровно тогда, когда сервер молчит, — то есть
 * в худшую минуту. Правило то же, что у остального кабинета: назвать событие
 * словами клиента и сказать, что делать дальше.
 *
 * Сами строки лежат в каталоге локали (`app/src/i18n/catalogs`) под ключом
 * `error.code.<код>`, где `<код>` — машинный код контракта как есть. Отсюда
 * два свойства сразу: перевод ошибки не требует ни строчки кода, а появление
 * нового кода на сервере видно приёмкой каталогов, а не клиентом.
 */
function byCode(code: string): string | null {
  const key = `error.code.${code}`;
  return hasMessage(key) ? t(key) : null;
}

/** Ошибка сети или прерванный запрос — ответа не было вовсе. */
export const OFFLINE_CODE = 'network_unavailable';

export function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Record<string, unknown>;
  return typeof p.code === 'string' && typeof p.status === 'number';
}

/**
 * Ответ пришёл, но разобрать его не удалось: не JSON или JSON битый.
 *
 * Функция общая, а не литерал по месту, потому что случай приходит с двух
 * сторон — и на ошибочном ответе, и на **успешном**. Успешный коварнее: `200`
 * с `text/html` присылает не сервис, а тот, кто стоит перед ним. У нас это
 * ровно один известный случай: пока `app.seeno.ru` указывает на край CDN, а не
 * на балансировщик кластера, правило SPA-фолбэка (`^/[^.]*$`) ловит и
 * `/api/v1/auth/session` тоже и отдаёт на него `index.html` с кодом 200.
 * Такой ответ уходил в `JSON.parse` и выбрасывал `SyntaxError` мимо всей
 * модели ошибок: `ApiError` не создавался, `code` не появлялся, и проверка
 * сессии вместо «войдите» показывала «Что-то не сработало на нашей стороне».
 *
 * Случай не разовый и не только про CDN: страница входа корпоративного прокси,
 * портал публичного Wi-Fi и заглушка провайдера отвечают ровно так же. Поэтому
 * разбирается не адрес и не топология, а тип содержимого: всё, что обещает
 * успех, но не является JSON, — нечитаемый ответ.
 */
export function unreadableProblem(status: number, requestId?: string): Problem {
  return {
    type: 'about:blank',
    title: t('error.unreadable.title'),
    status,
    code: 'internal',
    detail: t('error.unreadable.detail', { contact: LEGAL_ENTITY.contact }),
    request_id: requestId,
  };
}

/** Проблема, собранная на клиенте, когда ответа от сервера не было. */
export function offlineProblem(): Problem {
  return {
    type: 'about:blank',
    title: t('error.offline.title'),
    status: 0,
    code: OFFLINE_CODE,
    detail: t('error.offline.detail'),
  };
}

/**
 * Текст для человека. Никаких извинений и никакого «Error 500».
 *
 * Порядок объяснён в шапке файла: на языке-источнике рынка сначала `detail`
 * сервера — он знает числа и сроки; на остальных языках сначала строка
 * каталога, потому что `detail` придёт не на том языке.
 */
export function humanMessage(problem: Problem): string {
  const native = locale() === market().defaultLocale;
  if (native && problem.detail) return problem.detail;
  const fallback = byCode(problem.code);
  if (fallback) return fallback;
  if (problem.detail) return problem.detail;
  return problem.title || t('error.unknown');
}

/**
 * Заголовок ошибки — та же развилка, что у текста, и по той же причине.
 *
 * Раньше он стоял буквой на поверхности (`problem?.title ?? 'Не получилось'`), и
 * на арабском экране получилась бы русская шапка над переведённым текстом:
 * заголовок сервера пришёл бы как есть, а запасной остался бы русским. Оба
 * решения принимаются здесь, рядом друг с другом.
 */
export function problemTitle(problem: Problem | null | undefined): string {
  const native = locale() === market().defaultLocale;
  if (native && problem?.title) return problem.title;
  return t('error.title');
}

/**
 * Строковое поле проблемы, не описанное схемой поимённо.
 *
 * `Problem` объявлен с `additionalProperties: true`, и часть кодов доносит этим
 * продолжение разговора: `mfa_required` кладёт `mfa_token`, по которому идёт
 * второй фактор. Типов у таких полей нет по определению, поэтому доступ к ним
 * ровно один и он проверяет тип — иначе `undefined` уедет в тело запроса.
 */
export function extra(problem: Problem, key: string): string | undefined {
  const value = (problem as Record<string, unknown>)[key];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Ошибки по полям формы. На остальных проблемах поля отсутствуют. */
export function fieldErrors(problem: Problem): Record<string, string> {
  const out: Record<string, string> = {};
  for (const item of problem.errors ?? []) {
    if (item.field) out[item.field] = item.message ?? t('error.field');
  }
  return out;
}

/** Исключение, которое бросает клиент API. Несёт всю проблему целиком. */
export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(humanMessage(problem));
    this.name = 'ApiError';
    this.problem = problem;
  }

  get code(): string {
    return this.problem.code;
  }

  /** Идентификатор запроса — первое, что спросит поддержка. */
  get requestId(): string | undefined {
    return this.problem.request_id ?? undefined;
  }
}
